import {
  useEffect,
  useId,
  useRef,
  useState,
  type AnimationEvent as ReactAnimationEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import * as drawer from '@zag-js/drawer'
import { Portal, normalizeProps, useMachine } from '@zag-js/react'
import { sheetOpened } from './openSheets'

export interface SheetProps {
  /** Открыта ли шторка. */
  opened: boolean
  /** Имя диалога для диктора. */
  label: string
  onClose: () => void
  children: ReactNode
}

/**
 * Долгое нажатие на полосе и на затемнении — не меню. Android засчитывает
 * задержанный палец как долгое нажатие и перед системным меню даёт сильную
 * вибрацию; тянут шторку как раз с задержкой.
 */
const noMenu = (event: MouseEvent) => {
  event.preventDefault()
}

/**
 * Нижняя шторка на машине состояний `@zag-js/drawer` (Zag — команда Chakra
 * UI). Жест, инерция, порог закрытия, роль диалога, Escape, фокус и возврат
 * фокуса — её; разметка и вид — наши (FE-ADR-022).
 *
 * Тянется вся: за полосу сверху — всегда, за содержимое — когда оно
 * долистано до верха; ниже верха палец листает список. Закрывается
 * смахиванием (быстрым — с любого места, медленным — за половину высоты;
 * `closeThreshold` эта версия машины не читает), нажатием мимо панели и
 * Escape.
 *
 * Панель живёт в DOM всегда (закрытая — с `hidden`): эффекты машины ищут её
 * сразу при открытии. Содержимое — только пока шторка на экране: каждое
 * открытие с чистого листа.
 *
 * Прокрутку страницы под шторкой запираем сами, через `overflow: hidden` на
 * `html`: встроенная блокировка на iOS ставит body `position: fixed`, а от
 * этого WebView Telegram и Flutter сдвигали страницу (FE-ADR-020, 021).
 */
export function Sheet({ opened, label, onClose, children }: SheetProps) {
  const panel = useRef<HTMLDivElement>(null)
  const openedRef = useRef(opened)
  openedRef.current = opened
  const close = useRef(onClose)
  close.current = onClose

  const service = useMachine(drawer.machine, {
    id: useId(),
    open: opened,
    // Любое закрытие покупателем: смахивание, нажатие мимо панели, Escape.
    // Машина сообщает о нём и в конце ухода; родителю хватает одного раза —
    // пока он ещё считает шторку открытой.
    onOpenChange: ({ open }) => {
      if (!open && openedRef.current) close.current()
    },
    preventScroll: false,
    // Фокус — на саму панель, а не на первое поле: иначе в шторке с полем
    // ввода сразу выезжала бы клавиатура.
    initialFocusEl: () => panel.current,
  })
  const api = drawer.connect(service, normalizeProps)
  const closing = service.state.matches('closing')
  // Уходящая панель и затемнение уже помечены `hidden`; `data-closing` держит
  // их на экране до конца анимации (см. CSS).
  const exit = closing ? { 'data-closing': '' } : {}

  // Конец ухода машина ждёт событием `exitcomplete` на панели, но сама его не
  // шлёт. Шлём по концу нашей анимации; нет анимации (сокращённое движение,
  // тесты) — сразу: иначе шторка зависла бы «закрывающейся» поверх страницы.
  useEffect(() => {
    const node = panel.current
    if (!closing || !node) return
    const done = () => node.dispatchEvent(new CustomEvent('exitcomplete'))
    const name = getComputedStyle(node).animationName
    if (!name || name === 'none') {
      done()
      return
    }
    const onEnd = (event: AnimationEvent) => {
      if (event.target === node) done()
    }
    node.addEventListener('animationend', onEnd)
    return () => {
      node.removeEventListener('animationend', onEnd)
    }
  }, [closing])

  // Выезд доигран — больше он не повторяется. Под пальцем CSS гасит анимацию
  // (`data-swiping`), и без этой отметки, отпустив панель на месте (тянули
  // вверх или вниз не до порога), браузер запускал бы выезд заново: панель
  // прыгала вниз и выезжала ещё раз.
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    if (!api.open) setSettled(false)
  }, [api.open])
  const onAnimationEnd = (event: ReactAnimationEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && api.open) setSettled(true)
  }
  const rest = settled ? { 'data-settled': '' } : {}

  // Страница под шторкой не листается, но и не сдвигается. Заодно шторка
  // отмечается открытой — чтобы никто не открыл свою поверх (openSheets.ts).
  useEffect(() => {
    if (!api.open) return
    const root = document.documentElement
    const before = root.style.overflow
    root.style.overflow = 'hidden'
    const closed = sheetOpened()
    return () => {
      root.style.overflow = before
      closed()
    }
  }, [api.open])

  return (
    <Portal>
      <div
        {...api.getBackdropProps()}
        {...exit}
        {...rest}
        className="sheet-backdrop"
        onContextMenu={noMenu}
      />
      <div {...api.getPositionerProps()} className="sheet-positioner">
        <div
          {...api.getContentProps()}
          {...exit}
          {...rest}
          ref={panel}
          className="sheet"
          onAnimationEnd={onAnimationEnd}
        >
          <h2 {...api.getTitleProps()} className="visually-hidden">
            {label}
          </h2>
          <div {...api.getGrabberProps()} className="grab" onContextMenu={noMenu}>
            <span {...api.getGrabberIndicatorProps()} className="grab-bar" aria-hidden="true" />
          </div>
          {/* Прокручивается только этот блок: полоса сверху остаётся на месте. */}
          <div className="sheet-body">{api.open || closing ? children : null}</div>
        </div>
      </div>
    </Portal>
  )
}
